// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Form, Col, Button, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import InputNumber from "rc-input-number";

import { IAction, ActionType, raiseAction, IActionTypeChangeUser } from "actions";
import { DataState, MainPanel } from "types";
import { isSet } from "utils";
import { IState } from "states/states";
import { User } from "states/sitestates";
import { IRfid } from "states/ocppstates";

import SaveFeedback from "view/comp/SaveFeedback";
import ChargingPowerControl from "view/comp/ChargingPowerControl";

interface IRfidUserAdminProps {
  user: User | undefined;
  rfid: IRfid | undefined;
  isUserLoaded: boolean;
  dataState: DataState;
}

const mapStateToProps = (state: IState) => {
  const user = state.site.userList?.find((curUser) =>
    state.router.user ? state.router.user === curUser.id : state.site.user?.secId === curUser.secId,
  );
  if (user === undefined) {
    // this may happen directly at reload, thus do not log error
    // console.error("admin unidentified user", state.router.user, state.site.userList);
  }

  const props: IRfidUserAdminProps = {
    user,
    rfid: state.ocpp.rfid.find((curRfid) => curRfid.rfid === user?.secId),
    isUserLoaded: isSet(state.scheduler.userListExt.lastData),
    dataState: state.scheduler.saveState,
  };
  return props;
};

interface IRfidUserAdminState {
  userId: number | undefined;
  wasUserLoaded: boolean;
  isChanged: boolean;
  showChargerTariff?: boolean;
  carName?: string;
  carBattery?: number;
}

interface IRfidUserAdminDispatch {
  onOkClick: (id: number, state: IRfidUserAdminState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRfidUserAdminDispatch = {
    onOkClick: (id: number, state: IRfidUserAdminState) => {
      const payload: IActionTypeChangeUser = {
        id,
        settings: {
          showChargerTariff: state.showChargerTariff,
        },
        carName: state.carName,
        carBattery: state.carBattery,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_USER_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class RfidUserAdminComponent extends React.Component<
  IRfidUserAdminProps & IRfidUserAdminDispatch & WrappedComponentProps,
  IRfidUserAdminState
> {
  state: IRfidUserAdminState = {
    userId: undefined,
    wasUserLoaded: false,
    isChanged: false,
  };

  public componentDidUpdate() {
    if (
      this.props.user?.id !== this.state.userId ||
      this.state.wasUserLoaded !== this.props.isUserLoaded
    ) {
      this.setState({
        isChanged: false,
        userId: this.props.user?.id,
        wasUserLoaded: this.props.isUserLoaded,
        showChargerTariff: this.props.user?.settings?.showChargerTariff,
        carName: this.props.rfid?.carName,
        carBattery: this.props.rfid?.carBattery,
      });
    }
  }

  public render() {
    return (
      <div id="rfiduseradmin">
        <div className="savebox">
          {this.renderSaveChanges()}
          <SaveFeedback
            className="mb-2 mt-2"
            dataState={this.state.isChanged ? DataState.DIRTY : this.props.dataState}
          />
        </div>
        <h1 className="mt-4">
          <FormattedMessage id="User Settings" />
        </h1>
        {this.renderRfidSettings()}
        {(this.props.rfid?.accessRights?.isChargerPowerCtrlAllowed ?? false) && (
          <>
            <h1 className="mt-4">
              <FormattedMessage id="Charger Control Settings" />
            </h1>
            {this.renderControlSettings()}
          </>
        )}
      </div>
    );
  }

  private renderSaveChanges() {
    return (
      <div className="mb-2 mt-2">
        {this.state.isChanged && (
          <Button
            id="saveRfidUserAdmin"
            type="submit"
            variant="primary"
            className="me-2"
            onClick={(_ev) => {
              this.props.onOkClick(this.props.user!.id, this.state);
              this.setState({ isChanged: false });
            }}
          >
            <FormattedMessage id="Save Changes" />
          </Button>
        )}
        <Button id="cancelRfidUserAdmin" type="submit" variant="secondary">
          <Link to={`../${MainPanel.USER}`}>
            {this.state.isChanged ? (
              <FormattedMessage id="Cancel and Back" />
            ) : (
              <FormattedMessage id="Back to User Overview" />
            )}
          </Link>
        </Button>
      </div>
    );
  }

  private renderRfidSettings() {
    const isEditMode = true;

    return (
      <div id="rfidsettings" style={{ padding: 5 }}>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            <FormattedMessage id="Varibale Cost Tariff" />
          </Form.Label>
          <Col sm={9}>
            <Form.Group style={{ marginTop: 6 }}>
              <Form.Check
                id="showChargerTariff"
                className="link"
                checked={this.state.showChargerTariff === true}
                onChange={(event) => {
                  this.setState({
                    isChanged: true,
                    showChargerTariff: event.currentTarget.checked,
                  });
                }}
                label={<FormattedMessage id="show tariff in charger power graph" />}
              />
            </Form.Group>
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            <FormattedMessage id="Vehicle Identifier" />
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              id="carName"
              name="carName"
              value={this.state.carName}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  carName: event?.currentTarget.value,
                });
              }}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            <FormattedMessage id="Vehicle Battery" />
          </Form.Label>
          <Col sm={9}>
            <InputNumber
              id="carBattery"
              type="number"
              min={1}
              max={1000}
              value={this.state.carBattery}
              onChange={(value) => {
                this.setState({
                  isChanged: true,
                  carBattery: value ?? undefined, // null -> undefined
                });
              }}
            />{" "}
            kWh
          </Col>
        </Form.Group>
      </div>
    );
  }
  private renderControlSettings() {
    const isEditMode = true;

    return (
      <div id="chargercontrolsettings" style={{ padding: 5 }}>
        <p>
          <FormattedMessage id="This section sets the default settings for the user defined charger power control, which are automatically applied when a new charging session is started." />{" "}
          <FormattedMessage id="The settings can be adapted then for each running charging.session." />
        </p>
        <ChargingPowerControl isUserAdmin={true} />
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(RfidUserAdminComponent));
